import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { supabase } from '@/lib/supabase';
import { Lang, UserType } from '@/data/constants';

import { normalizeRole, parseRole } from '@/lib/roles';
import { isUnconfirmedError, sendVerificationLink } from '@/lib/accountState';
import { smsConsentPayload } from '@/data/smsConsent';

/**
 * Set to the new user's id for the duration of a registration so the profile
 * loader cannot race signUp's own INSERT and create a placeholder row with a
 * guessed account type. Module scope (not state) because the auth listener
 * fires outside this component's render cycle.
 */
let signupInFlight: string | null = null;


export interface Profile {
  id: string;
  email: string;
  user_type: UserType;
  first_name?: string | null;
  last_name?: string | null;
  company_name?: string | null;
  contact_name?: string | null;
  phone?: string | null;
  city?: string | null;
  state?: string | null;
  /** OPTIONAL — collected only in Profile Settings, never at registration. */
  license_number?: string | null;
  license_state?: string | null;
  insurance_carrier?: string | null;
  insurance_policy?: string | null;
  /** Set by admins only, after a real verification has been completed. */
  license_verified?: boolean | null;
  insurance_verified?: boolean | null;
  background_verified?: boolean | null;
  terms_accepted_at?: string | null;
  terms_version?: string | null;
  years_in_business?: number | null;
  employees?: number | null;
  service_areas?: string[] | null;
  trades?: string[] | null;
  preferred_language: Lang;
  status: string;
  is_admin: boolean;
  credits: number;
  /**
   * One-off $29 credits (mkt_profiles.extra_credits). Optional because some
   * profile reads project a narrower column list; treat a missing value as 0.
   * Spent only after the plan allowance for the cycle is used up.
   */
  extra_credits?: number;
  /**
   * Server-owned mailbox proof: 'unverified' | 'legacy_unverified' |
   * 'verified'. Written ONLY by the account-verification function from the
   * auth provider's own confirmation timestamp — never by the browser.
   */
  email_verification_state?: string | null;
  phone_verified?: boolean | null;
  account_state?: string | null;

}


interface AuthCtx {
  user: { id: string; email: string } | null;
  profile: Profile | null;
  loading: boolean;
  /**
   * `needsConfirmation` is returned when Supabase created the auth user but
   * issued NO SESSION because email confirmation is switched on. See signUp
   * for why this must never be treated as a completed registration.
   */
  signUp: (
    email: string,
    password: string,
    /** sms_opt_in = the user's own tick on the optional SMS consent box. */
    data: Partial<Profile> & { sms_opt_in?: boolean },
  ) => Promise<{ error?: string; needsConfirmation?: boolean }>;

  /**
   * `unconfirmed` = the credentials were refused because the ADDRESS is not
   * confirmed, not because the password is wrong. The sign-in page shows the
   * resend-confirmation recovery path for that case.
   */
  signIn: (email: string, password: string) => Promise<{ error?: string; unconfirmed?: boolean }>;
  signOut: () => Promise<void>;
  resetPassword: (email: string) => Promise<void>;
  refreshProfile: () => Promise<void>;
  updateProfile: (patch: Partial<Profile>) => Promise<void>;
}

const Ctx = createContext<AuthCtx | undefined>(undefined);


export const crmSync = async (payload: Record<string, unknown>) => {
  try {
    await supabase.functions.invoke('crm-sync', { body: payload });
  } catch (_e) {
    /* non blocking */
  }
};

/**
 * Adds / updates a CRM contact (with Preferred Language) through our own
 * server-side endpoint. No external vendor domain is referenced from the
 * browser bundle — required by the white-label policy.
 */
export const crmSubscribe = async (payload: {
  email: string;
  name?: string;
  phone?: string;
  sms_opt_in?: boolean;
  source: string;
  tags?: string[];
  language?: string;
}) => {
  try {
    await supabase.functions.invoke('crm-subscribe', { body: payload });
  } catch (_e) {
    /* non blocking */
  }
};


export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<{ id: string; email: string } | null>(null);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [loading, setLoading] = useState(true);

  /**
   * PROFILE LOADER.
   *
   * THE SIGNUP RACE THAT TURNED CONTRACTORS INTO HOMEOWNERS (P0, 2026-09-22).
   *
   * Reported: "I created the account as a contractor and it asked me to post
   * my new job — it didn't give the option to find jobs."
   *
   * Proven in the data: profile rows exist with company_name, contact_name and
   * trades (fields ONLY the contractor form collects) while user_type says
   * 'homeowner'. Example: company "A1 Best interiors", contact "JUNIOR",
   * trades ['painting'], user_type 'homeowner'.
   *
   * Sequence that produced it:
   *   1. signUp() returns a session, so onAuthStateChange fires SIGNED_IN and
   *      this loader ran CONCURRENTLY with signUp's own profile INSERT.
   *   2. The row was not committed yet, so this loader took the "no profile"
   *      branch and created a placeholder, guessing the role from auth
   *      metadata — which is NULL on this provider — and normalizeRole()
   *      defaults an unknown value to 'homeowner'.
   *   3. signUp's INSERT then conflicted, its upsert retry wrote the company
   *      fields, but the BEFORE UPDATE trigger mkt_profiles_guard FREEZES
   *      user_type for non-admin callers — so 'homeowner' became permanent.
   *
   * Two changes stop it for good:
   *   • while a signup is in flight this loader NEVER creates a row; signUp
   *     owns that write and carries the real role, and
   *   • outside signup a row is only created when the role is actually KNOWN
   *     from auth metadata. An unknown role is left alone rather than guessed,
   *     because a wrong guess is unfixable from the browser afterwards.
   */
  const loadProfile = useCallback(async (id: string, email: string) => {
    const { data } = await supabase.from('mkt_profiles').select('*').eq('id', id).maybeSingle();
    if (data) {
      // NOTE: admin rights are NEVER granted from the browser. The old
      // client-side "promote ADMIN_EMAIL to is_admin" write has been removed —
      // a BEFORE UPDATE trigger (mkt_profiles_guard) now freezes is_admin,
      // credits, status, user_type and the verification flags for every
      // non-admin caller, so changing them from the console or a crafted REST
      // call silently reverts. Admin rights are set server-side only
      // (admin-set-role edge function / direct DB migration).
      setProfile(data as Profile);

      /**
       * SELF-HEAL THE MAILBOX PROOF.  (P0 fix, 2026-09-22 — "my account is
       * active but it says confirm your email before buying a lead".)
       *
       * This project's auth provider AUTO-CONFIRMS new addresses, so
       * auth.users.email_confirmed_at is set the instant the account is
       * created and no "confirm your signup" link exists to click.
       * mkt_profiles.email_verification_state, however, defaulted to
       * 'unverified', and the only writer that promotes it (the
       * account-verification function) ran on sign-in ONLY — never after
       * registration, and never before the value was read. So a genuinely
       * confirmed account carried a stale 'unverified' flag, the purchase gate
       * refused it, and the welcome email said the opposite.
       *
       * Whenever a loaded profile still says 'unverified' we now ask the
       * SERVER to re-derive it from the provider's own timestamp and re-read
       * the row. The browser still never asserts the state itself — it only
       * asks the question at the moment the answer is needed.
       */
      if ((data as Profile).email_verification_state === 'unverified') {
        try {
          const { data: synced } = await supabase.functions.invoke('account-verification', {
            body: { action: 'sync_email' },
          });
          if (synced?.verified) {
            const { data: fresh } = await supabase
              .from('mkt_profiles')
              .select('*')
              .eq('id', id)
              .maybeSingle();
            if (fresh) setProfile(fresh as Profile);
          }
        } catch (_e) {
          /* non blocking: the gate re-checks with the server before refusing */
        }
      }
      return;
    }

    // A registration is mid-flight: signUp writes the row with the chosen
    // role. Creating one here is what corrupted the account type.
    if (signupInFlight === id) return;

    const { data: authData } = await supabase.auth.getUser();
    const meta = (authData?.user?.user_metadata || {}) as Record<string, unknown>;
    const knownRole = parseRole(meta.user_type as string | undefined);
    if (!knownRole) {
      // Role unknown -> do not invent one. The account keeps no profile row
      // until a trustworthy role is available, which is recoverable; a wrong
      // role is not (the update trigger freezes user_type).
      console.warn('[auth] profile row missing and no role in auth metadata — not creating a placeholder');
      return;
    }
    const fallback = {
      id,
      email,
      user_type: knownRole,
      first_name: (meta.first_name as string) || null,
      last_name: (meta.last_name as string) || null,
      company_name: (meta.company_name as string) || null,
      phone: (meta.phone as string) || null,
      preferred_language: (meta.preferred_language as string) || 'en',
    };
    const { data: created } = await supabase.from('mkt_profiles').insert(fallback).select('*').maybeSingle();
    if (created) setProfile(created as Profile);
  }, []);



  useEffect(() => {
    let mounted = true;
    /*
     * NEVER STUCK ON "LOADING". If the session read or the profile read failed
     * (weak phone signal, expired refresh token) `loading` used to stay true
     * forever, so every page that waits for auth showed a spinner with no way
     * out. Whatever happens, loading now ends and the visitor is treated as
     * signed out until the next auth event.
     */
    supabase.auth
      .getSession()
      .then(async ({ data }) => {
        const s = data.session;
        if (s?.user && mounted) {
          setUser({ id: s.user.id, email: s.user.email || '' });
          try {
            await loadProfile(s.user.id, s.user.email || '');
          } catch (e) {
            console.error('[auth] profile load failed', e);
          }
        }
      })
      .catch((e) => console.error('[auth] session read failed', e))
      .finally(() => {
        if (mounted) setLoading(false);
      });

    /*
     * DEFERRED PROFILE LOAD. Awaiting Supabase calls INSIDE onAuthStateChange
     * holds the client's auth lock; loadProfile's auth.getUser() then waits on
     * that same lock — a documented supabase-js deadlock that shows up as a
     * sign-in or sign-up spinner that never finishes. The load now runs on the
     * next tick, outside the callback. (It also lands after signUp has claimed
     * `signupInFlight`, which makes the role-race guard above even safer.)
     */
    const { data: sub } = supabase.auth.onAuthStateChange((_event, session) => {
      if (session?.user) {
        const u = { id: session.user.id, email: session.user.email || '' };
        setUser(u);
        window.setTimeout(() => {
          loadProfile(u.id, u.email).catch((e) => console.error('[auth] profile load failed', e));
        }, 0);
      } else {
        setUser(null);
        setProfile(null);
      }
    });
    return () => {
      mounted = false;
      sub.subscription.unsubscribe();
    };
  }, [loadProfile]);

  const signUp: AuthCtx['signUp'] = async (email, password, data) => {
    // The account type is written into the auth user metadata FIRST so it can
    // never be lost or defaulted to the wrong role later.
    const role = normalizeRole(data.user_type as string | undefined);
    /**
     * A2P 10DLC: SMS consent comes ONLY from the unchecked-by-default box on
     * the sign-up form. Not ticked (or no phone) => false. Never forced true.
     */
    const smsOptIn = data.sms_opt_in === true && !!String(data.phone || '').trim();

    const { data: res, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: {
          user_type: role,
          first_name: data.first_name || null,
          last_name: data.last_name || null,
          company_name: data.company_name || null,
          phone: data.phone || null,
          preferred_language: data.preferred_language || 'en',
        },
      },
    });
    if (error) return { error: error.message };
    const id = res.user?.id;
    if (!id) return { error: 'Signup failed' };
    // Claim the profile write for this registration: the auth listener fires
    // immediately and must not create a placeholder row with a guessed role.
    signupInFlight = id;


    /**
     * NO SESSION == EMAIL CONFIRMATION IS REQUIRED.  (P0 fix, 2026-09-22)
     *
     * REPORTED: "I created an account and never got a confirmation." The
     * account genuinely did not exist — mkt_profiles had no row for that
     * address at all.
     *
     * WHY: when Supabase is configured to confirm addresses, `auth.signUp`
     * returns a user but NO session, so the very next statement — the
     * mkt_profiles INSERT — ran as `anon`. The RLS policy p_profiles_ins
     * requires `jwt.sub = id`, so it was rejected; the upsert retry was
     * rejected for the same reason; and because both errors were swallowed the
     * code then fabricated a LOCAL profile object, flipped the UI to a
     * signed-in state and navigated to the dashboard. The user saw a
     * "working" account that existed nowhere, got no email, and every
     * subsequent request behaved as a signed-out visitor.
     *
     * Now the caller is told the truth. The profile row is created on the
     * FIRST authenticated load after the address is confirmed — loadProfile()
     * already rebuilds it from the auth metadata written above, which is why
     * the role chosen here survives confirmation.
     */
    if (!res.session) {
      // No session means no profile write can race here — release the guard so
      // a later confirmation in this same tab can build the row normally.
      signupInFlight = null;
      // CRM follow-up runs in the BACKGROUND: the "Check your email" screen
      // must appear immediately, not after two CRM round-trips on a phone.
      const pendingName =
        data.company_name || `${data.first_name || ''} ${data.last_name || ''}`.trim() || email;
      void (async () => {
        await crmSubscribe({
          email,
          name: pendingName,
          phone: data.phone || undefined,
          sms_opt_in: smsOptIn,
          source: `register-pending-${role}`,
          tags: [role, 'pending-confirmation', `lang:${data.preferred_language || 'en'}`],
          language: data.preferred_language || 'en',
        });
        /**
         * UNVERIFIED / ABANDONED REGISTRATION — tagged "Unverified" +
         * "Registration Started" so the confirmation-reminder workflow can chase them.
         */
        try {
          const { submitForm } = await import('@/lib/crmForms');
          await submitForm({
            formType: 'registration-started',
            email,
            name: pendingName,
            phone: data.phone || undefined,
            language: (data.preferred_language === 'es' ? 'es' : 'en') as Lang,
            userType: role,
            companyName: data.company_name || undefined,
            city: data.city || undefined,
            state: data.state || undefined,
            trade: Array.isArray(data.trades) && data.trades.length ? String(data.trades[0]) : undefined,
            ...smsConsentPayload(smsOptIn),
            consent: true,
            registrationStatus: 'awaiting-confirmation',
            tags: ['GigGone Unverified', `role:${role}`, 'nurture:abandoned-registration'],
          });
        } catch (_e) {
          /* non blocking */
        }
      })();

      return { needsConfirmation: true };
    }


    // PRIVILEGED FIELDS ARE NEVER SENT FROM THE BROWSER.
    // is_admin, credits, extra_credits, status and the verification flags are
    // deliberately absent from this payload. A BEFORE INSERT trigger
    // (mkt_profiles_insert_guard) recomputes them server-side from the trusted
    // auth identity + the mkt_admin_emails allowlist, so even a crafted REST
    // call that sets is_admin:true is neutralised before the row is written.
    const row = {
      id,
      email,
      user_type: role,
      first_name: data.first_name || null,
      last_name: data.last_name || null,
      company_name: data.company_name || null,
      contact_name: data.contact_name || null,
      phone: data.phone || null,
      city: data.city || null,
      state: data.state || null,
      // NOTE: license_number is intentionally NOT collected at registration.
      // It is optional and can be added later in Profile Settings.
      years_in_business: data.years_in_business || null,
      employees: data.employees || null,
      service_areas: data.service_areas || [],
      trades: data.trades || [],
      preferred_language: data.preferred_language || 'en',
      terms_accepted_at: data.terms_accepted_at || new Date().toISOString(),
      terms_version: data.terms_version || null,
    };
    const { data: inserted, error: insErr } = await supabase
      .from('mkt_profiles')
      .insert(row)
      .select('*')
      .maybeSingle();
    let saved = inserted;
    if (insErr || !saved) {
      // Retry once as an upsert so the account can never end up without a
      // profile row (which is what previously caused the wrong-role fallback).
      const { data: upserted } = await supabase
        .from('mkt_profiles')
        .upsert(row, { onConflict: 'id' })
        .select('*')
        .maybeSingle();
      saved = upserted;
    }
    /**
     * If BOTH writes failed we must not pretend the account exists — that is
     * precisely the failure mode that produced a phantom signed-in session
     * with no database row. Report it so the user can retry instead of
     * discovering days later that they have no account.
     */
    if (!saved) {
      console.error('[auth] profile row could not be written after signup', insErr);
      signupInFlight = null;
      return { error: insErr?.message || 'Your account could not be finished. Please try again.' };
    }
    /**
     * ROLE DRIFT DETECTOR.  (P0 fix — "I registered as a contractor and it
     * asked me to post a job.")
     *
     * If the stored row does not carry the role that was chosen on the form,
     * the account is already in the broken state that produced that report, so
     * say so loudly instead of dropping the user into the wrong dashboard. The
     * dashboard then offers the one-click repair (AccountRoleFix), which is
     * decided and written server-side by account-role-repair.
     */
    const storedRole = (saved as Profile).user_type;
    if (storedRole !== role) {
      console.error('[auth] role drift at signup', { chosen: role, stored: storedRole });
      await supabase.from('mkt_security_events').insert({
        event_type: 'signup_role_drift',
        severity: 'high',
        outcome: 'denied',
        user_id: id,
        email,
        detail: `Chosen ${role}, stored ${storedRole}`,
        metadata: { chosen: role, stored: storedRole },
      });
    }
    // Always trust the row the DATABASE returned (post-trigger), never the
    // payload we sent — that is what makes the admin flag server-authoritative.
    setProfile(saved as Profile);
    setUser({ id, email });
    signupInFlight = null;

    /**
     * EMAIL OWNERSHIP LINK.  (Real verification, 2026-09-24.)
     *
     * The auth provider auto-confirms every address, which proves nothing
     * about who owns the mailbox. GIGGONE therefore sends its OWN signed,
     * one-time verification link (account-verification -> send-transactional-
     * email, template email_verification). The account starts 'unverified'
     * and only the verify-email function can flip it to 'verified' once the
     * link is opened. Non-blocking: a mail problem must never fail a
     * registration — the dashboard and the purchase gate both offer a resend.
     */
    void sendVerificationLink('signup');



    // Immutable record that this user accepted the marketplace terms.
    // Background: it must never hold "Creating account…" on a slow phone.
    void Promise.resolve(
      supabase.from('mkt_acknowledgements').insert({
        user_id: id,
        email,
        context: `registration-${row.user_type}`,
        terms_version: row.terms_version || 'unversioned',
        language: row.preferred_language,
        detail: 'Accepted Terms of Service, Privacy Policy and marketplace disclaimer at signup',
      }),
    ).catch(() => undefined);

    const displayName = row.company_name || `${row.first_name || ''} ${row.last_name || ''}`.trim() || email;

    /**
     * CRM / MARKETING SYNC RUNS IN THE BACKGROUND.  (Journey review 2026-09-30)
     *
     * These calls used to be awaited one after another BEFORE signUp returned,
     * so a buyer who pressed "Create account & continue to payment" stared at
     * "Creating account..." for several extra seconds while we talked to the
     * CRM — exactly the moment people give up. The account row, the terms
     * acknowledgement and the verification email are already done above;
     * nothing below affects the purchase, so the page moves on immediately and
     * this finishes on its own. Every call is guarded; the CRM layer retries
     * server-side.
     */
    void (async () => {
      try {
        await crmSubscribe({
          email,
          name: displayName,
          phone: row.phone || undefined,
          sms_opt_in: smsOptIn,
          source: `register-${row.user_type}`,
          tags: [row.user_type, `lang:${row.preferred_language}`],
          language: row.preferred_language,
        });
      } catch (_e) {
        /* non blocking */
      }

      try {
        const { submitForm, crmActivity } = await import('@/lib/crmForms');
        const primaryTrade = Array.isArray(row.trades) && row.trades.length ? String(row.trades[0]) : '';
        const formType =
          row.user_type === 'homeowner'
            ? 'register-homeowner'
            : row.user_type === 'gc'
            ? 'register-gc'
            : 'register-subcontractor';

        await submitForm({
          formType: formType as 'register-homeowner' | 'register-gc' | 'register-subcontractor',
          email,
          name: displayName,
          phone: row.phone || undefined,
          language: (row.preferred_language === 'es' ? 'es' : 'en') as Lang,
          userType: row.user_type,
          companyName: row.company_name || undefined,
          userId: id,
          // Only what the user ticked on the sign-up form — never forced.
          ...smsConsentPayload(smsOptIn),
          consent: true,
          city: row.city || undefined,
          state: row.state || undefined,
          trade: primaryTrade || undefined,
          tradeSlug: primaryTrade || undefined,
          registrationStatus: 'completed',
          tags: [`role:${row.user_type}`, ...(row.trades || []).slice(0, 5).map((tr: string) => `trade:${tr}`)],
          fields: {
            service_areas: (row.service_areas || []).slice(0, 5).join(', '),
            years_in_business: row.years_in_business ?? '',
            employees: row.employees ?? '',
          },
        });

        // Behavioural event so the "Completed Registration" workflow can fire.
        void crmActivity('registration-completed', {
          email,
          name: displayName,
          phone: row.phone || undefined,
          language: (row.preferred_language === 'es' ? 'es' : 'en') as Lang,
          userType: row.user_type,
          userId: id,
          city: row.city || undefined,
          state: row.state || undefined,
          trade: primaryTrade || undefined,
        });
      } catch (_e) {
        /* the account is already created — CRM sync is retried server-side */
      }

      await crmSync({
        event: 'welcome',
        email,
        name: displayName,
        phone: row.phone,
        language: row.preferred_language,
        userType: row.user_type,
      });
      try {
        await supabase.from('mkt_activity').insert({ user_id: id, action: 'register', detail: row.user_type });
      } catch (_e) {
        /* non blocking */
      }
    })();

    return {};
  };


  /**
   * SIGN IN.
   *
   * `unconfirmed` is returned when the address exists but has never been
   * confirmed. Login.tsx shows the specific "confirm your email" recovery path
   * for that case instead of the generic "email or password is incorrect",
   * which previously left those users with no way back in without support.
   */
  const signIn: AuthCtx['signIn'] = async (email, password) => {
    const { data, error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) return { error: error.message, unconfirmed: isUnconfirmedError(error.message) };
    if (data.user) {
      setUser({ id: data.user.id, email: data.user.email || '' });
      // STABILITY: the password was ACCEPTED at this point. A flaky profile read
      // used to throw out of here, and the sign-in page then said "connection
      // dropped — tap again" to someone who was in fact signed in. The auth
      // listener re-loads the profile anyway, so a failure here is harmless.
      try {
        await loadProfile(data.user.id, data.user.email || '');
      } catch (e) {
        console.error('[auth] profile load after sign-in failed', e);
      }
      // Promote the verification state server-side when the provider has
      // already confirmed this mailbox. The browser never sets it itself.
      supabase.functions
        .invoke('account-verification', { body: { action: 'sync_email' } })
        .catch(() => undefined);
    }
    return {};
  };


  const signOut = async () => {
    await supabase.auth.signOut();
    setUser(null);
    setProfile(null);
  };

  const resetPassword = async (email: string) => {
    // Send the user to the dedicated reset page (localized), where the recovery
    // token in the URL hash is exchanged for a session before the new password
    // is saved. Falls back to /en/reset-password when no language prefix exists.
    const seg = window.location.pathname.split('/')[1];
    const lang = seg === 'es' ? 'es' : 'en';
    const target = lang === 'es' ? '/es/restablecer-contrasena' : '/en/reset-password';
    await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}${target}`,
    });
  };


  const refreshProfile = async () => {
    if (user) await loadProfile(user.id, user.email);
  };

  const updateProfile = async (patch: Partial<Profile>) => {
    if (!user) return;
    await supabase.from('mkt_profiles').update(patch).eq('id', user.id);
    setProfile((p) => (p ? { ...p, ...patch } : p));
  };

  return (
    <Ctx.Provider value={{ user, profile, loading, signUp, signIn, signOut, resetPassword, refreshProfile, updateProfile }}>
      {children}
    </Ctx.Provider>
  );
};

export const useAuth = (): AuthCtx => {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error('useAuth must be used inside AuthProvider');
  return ctx;
};
